import { Seo, buildArticleSchema } from '../../core/seo';

const cookieArticleSchema = buildArticleSchema({
  headline: 'Cookie Policy',
  description:
    'How HirePanther uses cookies and similar technologies on our website and apps.',
  url: 'https://uat.hirepanther.ca/cookie-policy',
  datePublished: '2025-12-01',
  dateModified: '2025-12-01',
  publisher: {
    '@type': 'Organization',
    name: 'HirePanther',
    url: 'https://uat.hirepanther.ca',
  },
});

const CookiePolicy = () => {
  return (
    <div className=" text-[#284F60] min-h-screen py-12 px-6">
      <Seo
        title="Cookie Policy"
        description="How HirePanther uses cookies and similar technologies on our website and apps."
        canonicalPath="/cookie-policy"
        structuredData={cookieArticleSchema}
      />
      <div className="min-h-screen bg-white text-[#284F60]  px-6 py-12 md:px-16 lg:px-32">
        {/* Page Header */}
        <header className="text-left mb-12">
          <h1 className="text-4xl font-bold text-[#284F60] mb-2">
            Cookie Policy
          </h1>
          <p className="text-sm text-gray-500 mt-3">
            Effective Date: December 01, 2025 | Last Updated: December 01, 2025
          </p>
        </header>

        {/* Intro */}
        <section className="mb-10">
          <p className="text-lg leading-relaxed">
            This Cookie Policy explains how HirePanther (“we,” “our,” or “us”)
            uses cookies and similar technologies when you visit our website,
            apps, and provider portal (the “Platform”).
          </p>
        </section>

        {/* Section 1 */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold text-[#2D617A] mb-4">
            1. What Are Cookies?
          </h2>
          <p className="mb-4">
            Cookies are small text files stored on your device when you visit a
            website. They allow us to recognize your browser, remember your
            preferences, and improve your experience.
          </p>
          <p>
            We also use similar technologies such as web beacons, pixels, and
            local storage (together, “cookies”).
          </p>
        </section>

        {/* Section 2 */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold text-[#2D617A] mb-4">
            2. Types of Cookies We Use
          </h2>

          <h3 className="text-xl font-medium text-[#2D617A] mt-4">
            a) Strictly Necessary Cookies
          </h3>
          <ul className="list-disc list-inside space-y-2 mt-2 ml-4">
            <li>
              Required for the Platform to function (e.g., login, security,
              payments).
            </li>
            <li>Cannot be turned off.</li>
          </ul>

          <h3 className="text-xl font-medium text-[#2D617A] mt-6">
            b) Performance & Analytics Cookies
          </h3>
          <ul className="list-disc list-inside space-y-2 mt-2 ml-4">
            <li>Collect information about how you use the Platform.</li>
            <li>Help us improve speed, usability, and features.</li>
            <li>Example: Google Analytics.</li>
          </ul>

          <h3 className="text-xl font-medium text-[#2D617A] mt-6">
            c) Functionality Cookies
          </h3>
          <ul className="list-disc list-inside space-y-2 mt-2 ml-4">
            <li>Remember your settings (e.g., language EN/FR, location).</li>
            <li>Provide a more personalized experience.</li>
          </ul>

          <h3 className="text-xl font-medium text-[#2D617A] mt-6">
            d) Advertising & Marketing Cookies
          </h3>
          <ul className="list-disc list-inside space-y-2 mt-2 ml-4">
            <li>
              Used to deliver relevant promotions and measure campaign
              performance.
            </li>
            <li>
              May be placed by us or trusted third parties (e.g., social media
              platforms, ad networks).
            </li>
          </ul>
        </section>

        {/* Section 3 */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold text-[#2D617A] mb-4">
            3. How We Use Cookies
          </h2>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>Enable secure login and account management.</li>
            <li>Process bookings and payments.</li>
            <li>Remember your preferences (language, region, currency).</li>
            <li>Analyze usage to improve the Platform.</li>
            <li>Deliver personalized ads and promotions.</li>
          </ul>
        </section>

        {/* Section 4 */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold text-[#2D617A] mb-4">
            4. Third-Party Cookies
          </h2>
          <p className="mb-4">
            Some cookies are set by third parties we work with, such as:
          </p>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>Analytics providers (Google, Mixpanel, etc.).</li>
            <li>Payment partners (Stripe, Interac).</li>
            <li>Advertising platforms (Facebook, LinkedIn, TikTok).</li>
          </ul>
          <p className="mt-4">
            These third parties may collect data to improve their own services.
          </p>
        </section>

        {/* Section 5 */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold text-[#2D617A] mb-4">
            5. Your Choices
          </h2>

          <h3 className="text-xl font-medium text-[#2D617A] mt-4">
            a) Cookie Banner & Consent
          </h3>
          <ul className="list-disc list-inside space-y-2 mt-2 ml-4">
            <li>
              When you first visit HirePanther, you will see a cookie banner.
            </li>
            <li>
              By continuing to use the Platform, you agree to our use of
              cookies.
            </li>
          </ul>

          <h3 className="text-xl font-medium text-[#2D617A] mt-6">
            b) Managing Cookies
          </h3>
          <ul className="list-disc list-inside space-y-2 mt-2 ml-4">
            <li>Adjust your browser settings to block or delete cookies.</li>
            <li>
              Opt out of targeted ads through the Digital Advertising Alliance
              of Canada.
            </li>
            <li>
              Use “Do Not Track” (DNT) settings in your browser (where
              supported).
            </li>
          </ul>
          <p className="mt-2 text-[#284F60]">
            Note: Disabling certain cookies may impact functionality (e.g.,
            logging in, payments).
          </p>
        </section>

        {/* Section 6 */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold text-[#2D617A] mb-4">
            6. Data Protection
          </h2>
          <p>
            Any personal information collected through cookies is handled in
            line with our Privacy Policy.
          </p>
        </section>

        {/* Section 7 */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold text-[#2D617A] mb-4">
            7. Updates to This Policy
          </h2>
          <p>
            We may update this Cookie Policy from time to time. Updates will be
            posted at /legal/cookies with a new effective date.
          </p>
        </section>

        {/* Contact */}
        <section>
          <h2 className="text-2xl font-semibold text-[#2D617A] mb-4">
            8. Contact Us
          </h2>
          <p>If you have questions about cookies or your choices:</p>
          <p className="mt-2">
            <a
              href="mailto:privacy@hirepanther.ca"
              className="text-blue-500 hover:underline"
            >
              privacy@hirepanther.ca
            </a>
          </p>
        </section>
      </div>
    </div>
  );
};

export default CookiePolicy;
